import { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Avatar,
  Box,
  useTheme,
  Card,
  CardContent,
  Skeleton,
  Chip,
  Fade,
  Tooltip,
  LinearProgress,
  IconButton,
  Stack,
  Divider as MuiDivider,
  useMediaQuery
} from '@mui/material';

// Iconos
import SingleBedIcon from '@mui/icons-material/SingleBed';
import KingBedIcon from '@mui/icons-material/KingBed';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import BuildIcon from '@mui/icons-material/Build';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';

import { gridSpacing } from 'store/constant';
import { getApi } from 'views/services/api';
import TotalRoomsCard from './TotalRooms';
import TotalCustomersCard from './TotalCustomers';
import FoodItems from './TotalFoodItems';
import TotalReservationCard from './TotalReseravtions';
import ReserveRoom from 'views/Room/ReserveRoom';
import { useNavigate } from 'react-router';

// ==============================|| CONSTANTES Y CONFIGURACIÓN ||============================== //

// Mapeo profesional de iconos por tipo de habitación
const RoomTypeIcons = {
  single: <SingleBedIcon sx={{ fontSize: '2rem' }} />,
  double: <KingBedIcon sx={{ fontSize: '2rem' }} />,
  triple: <KingBedIcon sx={{ fontSize: '2rem' }} />,
  family: <KingBedIcon sx={{ fontSize: '2rem' }} />
};

// Configuración profesional de colores de estado
const getStatusConfiguration = (status, theme) => {
  const configurations = {
    // Estado de ocupación
    occupied: {
      background: 'rgba(211, 47, 47, 0.08)',
      border: theme.palette.error.light,
      text: theme.palette.error.dark,
      icon: theme.palette.error.main,
      chip: 'error',
      label: 'Ocupada'
    },

    // Estados de limpieza
    dirty: {
      background: 'rgba(237, 108, 2, 0.08)',
      border: theme.palette.warning.main,
      text: theme.palette.warning.dark,
      icon: theme.palette.warning.main,
      chip: 'warning',
      label: 'Requiere Limpieza'
    },

    clean: {
      background: 'rgba(46, 125, 50, 0.08)',
      border: theme.palette.success.light,
      text: theme.palette.success.dark,
      icon: theme.palette.success.main,
      chip: 'success',
      label: 'Disponible'
    },

    'in-progress': {
      background: 'rgba(2, 136, 209, 0.08)',
      border: theme.palette.info.light,
      text: theme.palette.info.dark,
      icon: theme.palette.info.main,
      chip: 'info',
      label: 'En Limpieza'
    },

    'do-not-disturb': {
      background: 'rgba(103, 58, 183, 0.08)',
      border: theme.palette.secondary.light,
      text: theme.palette.secondary.dark,
      icon: theme.palette.secondary.main,
      chip: 'secondary',
      label: 'No Molestar'
    },

    'out-of-service': {
      background: 'rgba(97, 97, 97, 0.08)',
      border: theme.palette.grey[600],
      text: theme.palette.grey[700],
      icon: theme.palette.grey[600],
      chip: 'default',
      label: 'Fuera de Servicio'
    },

    // Reserva pendiente (sin check-in) — Azul índigo para diferenciar de 'dirty' (ámbar)
    pending: {
      background: 'rgba(63, 81, 181, 0.08)',
      border: '#7986CB',
      text: '#283593',
      icon: '#3F51B5',
      chip: 'info',
      label: 'Reserva Pendiente'
    },

    // Reserva pendiente pero la habitación aún está sucia — Naranja oscuro (Deep Orange)
    'pending-dirty': {
      background: 'rgba(216, 67, 21, 0.08)',
      border: '#D84315',
      text: '#BF360C',
      icon: '#D84315',
      chip: 'warning',
      label: 'Pendiente (Sucia)'
    }
  };

  return configurations[status] || configurations.clean;
};

// ==============================|| COMPONENTE DE TEMPORIZADOR PROFESIONAL ||============================== //

const ProfessionalCountdownTimer = ({ checkOutTime, stayType }) => {
  const [timeLeft, setTimeLeft] = useState({});
  const [isExpired, setIsExpired] = useState(false);
  const [progress, setProgress] = useState(100);
  const theme = useTheme();

  useEffect(() => {
    if (!checkOutTime) return;

    const calculateTimeLeft = () => {
      try {
        const checkOutDate = new Date(checkOutTime);
        const now = new Date();
        const totalDifference = checkOutDate.getTime() - now.getTime();

        // Calcular progreso para estancias por horas
        if (stayType === 'partial') {
          const threeHoursInMs = 3 * 60 * 60 * 1000;
          const elapsed = threeHoursInMs - totalDifference;
          const progressPercentage = Math.max(0, Math.min(100, (elapsed / threeHoursInMs) * 100));
          setProgress(100 - progressPercentage);
        }

        if (totalDifference <= 0) {
          setIsExpired(true);
          return { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }

        return {
          days: Math.floor(totalDifference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((totalDifference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((totalDifference / 1000 / 60) % 60),
          seconds: Math.floor((totalDifference / 1000) % 60)
        };
      } catch (error) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);

    return () => clearInterval(timer);
  }, [checkOutTime, stayType]);

  if (stayType === 'partial') {
    return (
      <Box
        sx={{
          position: 'relative',
          backgroundColor: isExpired ? theme.palette.error.light : 'rgba(237, 108, 2, 0.08)',
          borderRadius: '8px',
          padding: '6px 12px',
          border: `1px solid ${isExpired ? theme.palette.error.main : theme.palette.warning.main}`,
          minWidth: '100px'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <HourglassEmptyIcon
            sx={{
              fontSize: '1rem',
              color: isExpired ? theme.palette.error.main : theme.palette.warning.main
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: isExpired ? theme.palette.error.main : theme.palette.warning.dark,
              fontFamily: 'monospace'
            }}
          >
            {isExpired
              ? 'VENCIDO'
              : `${String(timeLeft.hours).padStart(2, '0')}:${String(timeLeft.minutes).padStart(2, '0')}:${String(
                  timeLeft.seconds
                ).padStart(2, '0')}`}
          </Typography>
        </Stack>

        {/* Barra de progreso para estancias parciales */}
        {!isExpired && stayType === 'partial' && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 2,
              backgroundColor: theme.palette.warning.light,
              '& .MuiLinearProgress-bar': {
                backgroundColor: theme.palette.warning.main
              }
            }}
          />
        )}
      </Box>
    );
  }

  // Para estancias nocturnas
  const showDays = timeLeft.days > 0;
  const urgentThreshold = timeLeft.days === 0 && timeLeft.hours < 3;

  return (
    <Tooltip
      title={`Check-out: ${new Date(checkOutTime).toLocaleString('es-MX', {
        dateStyle: 'short',
        timeStyle: 'short'
      })}`}
    >
      <Box
        sx={{
          backgroundColor: isExpired ? 'rgba(211, 47, 47, 0.08)' : urgentThreshold ? 'rgba(237, 108, 2, 0.08)' : 'rgba(2, 136, 209, 0.08)',
          borderRadius: '8px',
          padding: '6px 12px',
          border: `1px solid ${
            isExpired ? theme.palette.error.main : urgentThreshold ? theme.palette.warning.main : theme.palette.info.light
          }`,
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'scale(1.05)'
          }
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <CalendarTodayIcon
            sx={{
              fontSize: '1rem',
              color: isExpired ? theme.palette.error.main : urgentThreshold ? theme.palette.warning.main : theme.palette.info.main
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: isExpired ? theme.palette.error.main : urgentThreshold ? theme.palette.warning.dark : theme.palette.info.dark
            }}
          >
            {isExpired ? 'VENCIDO' : showDays ? `${timeLeft.days}d ${timeLeft.hours}h` : `${timeLeft.hours}h ${timeLeft.minutes}m`}
          </Typography>
        </Stack>
      </Box>
    </Tooltip>
  );
};

// ==============================|| COMPONENTE DE TEMPORIZADOR SUCIO ||============================== //

const DirtyRoomTimer = ({ dirtySince }) => {
  const [timeElapsed, setTimeElapsed] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const theme = useTheme();

  useEffect(() => {
    if (!dirtySince) return;

    const calculateTimeElapsed = () => {
      const start = new Date(dirtySince);
      const now = new Date();
      const diff = now.getTime() - start.getTime();

      if (diff < 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / 1000 / 60) % 60),
        seconds: Math.floor((diff / 1000) % 60)
      };
    };

    setTimeElapsed(calculateTimeElapsed());
    const timer = setInterval(() => setTimeElapsed(calculateTimeElapsed()), 1000);

    return () => clearInterval(timer);
  }, [dirtySince]);

  if (!dirtySince) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: 'rgba(237, 108, 2, 0.08)',
        borderRadius: '8px',
        padding: '6px 12px',
        border: `1px solid ${theme.palette.warning.main}`,
        minWidth: '100px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <CleaningServicesIcon
          sx={{
            fontSize: '1rem',
            color: theme.palette.warning.main
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: theme.palette.warning.dark,
            fontFamily: 'monospace'
          }}
        >
          {timeElapsed.days > 0 ? `${timeElapsed.days}d ` : ''}
          {String(timeElapsed.hours).padStart(2, '0')}:{String(timeElapsed.minutes).padStart(2, '0')}:
          {String(timeElapsed.seconds).padStart(2, '0')}
        </Typography>
      </Stack>
    </Box>
  );
};

// ==============================|| COMPONENTE DE HABITACIÓN PROFESIONAL ||============================== //

const ProfessionalRoomCard = ({ room, reservation, pendingReservation, onClick, theme, nightlyType, cleaningBufferMinutes = 30 }) => {
  const isOccupied = room.bookingStatus === 'active';
  const isPartialStay = reservation?.stayType === 'partial';
  const housekeepingStatus = reservation?.housekeepingStatus || room.housekeepingStatus || 'clean';

  // Determinar si la reserva pendiente ya debe bloquear la habitación
  // Se bloquea cuando: ahora >= checkInDate - bufferMinutes
  const now = new Date();
  let isPendingToday = false;
  let isPendingFuture = false;

  if (!isOccupied && pendingReservation) {
    const pendingCheckIn = new Date(pendingReservation.checkInDate);
    const blockThreshold = new Date(pendingCheckIn.getTime() - (cleaningBufferMinutes * 60 * 1000));

    if (now >= blockThreshold) {
      isPendingToday = true;  // Ya es hora de bloquear (check-in próximo o pasado)
    } else {
      isPendingFuture = true; // Aún falta tiempo, mostrar solo indicador
    }
  }

  // Determinar configuración visual
  const statusConfig = isOccupied
    ? getStatusConfiguration('occupied', theme)
    : isPendingToday
      ? housekeepingStatus === 'dirty'
        ? getStatusConfiguration('pending-dirty', theme)
        : getStatusConfiguration('pending', theme)
      : getStatusConfiguration(housekeepingStatus, theme);

  // Bloqueamos el clic para habitaciones sucias o fuera de servicio
  // Así evitamos que la recepcionista pueda hacer check-in antes de que limpieza termine.
  const isClickable = housekeepingStatus !== 'out-of-service' && housekeepingStatus !== 'dirty';

  return (
    <Fade in={true} timeout={500}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          cursor: isClickable ? 'pointer' : 'not-allowed',
          opacity: isClickable ? 1 : 0.7,
          transition: 'all 0.3s ease',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          position: 'relative',
          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.03)',
          '&:hover': isClickable
            ? {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px 0 rgba(0,0,0,0.08)',
                borderColor: statusConfig.border
              }
            : {},
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: statusConfig.border,
            transition: 'height 0.3s ease'
          },
          '&:hover::before': isClickable
            ? {
                height: '6px'
              }
            : {}
        }}
        onClick={isClickable ? onClick : undefined}
      >
        {/* Contenido de la tarjeta */}
        <Box sx={{ p: 2.5 }}>
          {/* Encabezado con número de habitación y estado */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  letterSpacing: '-0.5px'
                }}
              >
                Habitación {room.roomNo}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  textTransform: 'capitalize',
                  mt: 0.5
                }}
              >
                {room.roomType === 'single'
                  ? 'Individual'
                  : room.roomType === 'double'
                  ? 'Doble'
                  : room.roomType === 'triple'
                  ? 'Triple'
                  : room.roomType === 'family'
                  ? 'Familiar'
                  : room.roomType}
              </Typography>
            </Box>

            <Avatar
              sx={{
                width: 40,
                height: 40,
                backgroundColor: statusConfig.background,
                color: statusConfig.icon
              }}
            >
              {RoomTypeIcons[room.roomType.toLowerCase()] || <KingBedIcon />}
            </Avatar>
          </Stack>

          {/* Información de precio y estado */}
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Chip
                label={
                  isPartialStay
                    ? `$${room.partialAmount || '0'} / 3hrs`
                    : nightlyType && room.pricing?.find((p) => p.stayTypeId === nightlyType._id)
                    ? `$${room.pricing.find((p) => p.stayTypeId === nightlyType._id).amount} / noche`
                    : `$${room.amount} / noche`
                }
                size="small"
                sx={{
                  backgroundColor: statusConfig.background,
                  color: statusConfig.text,
                  border: `1px solid ${statusConfig.border}`,
                  fontWeight: 600,
                  '& .MuiChip-label': {
                    px: 1.5
                  }
                }}
              />

              <Chip
                icon={
                  isOccupied ? (
                    isPartialStay ? (
                      <AccessTimeIcon />
                    ) : null
                  ) : isPendingToday ? (
                    <HourglassEmptyIcon />
                  ) : housekeepingStatus === 'dirty' ? (
                    <CleaningServicesIcon />
                  ) : housekeepingStatus === 'in-progress' ? (
                    <CleaningServicesIcon />
                  ) : housekeepingStatus === 'out-of-service' ? (
                    <BuildIcon />
                  ) : housekeepingStatus === 'do-not-disturb' ? (
                    <DoNotDisturbIcon />
                  ) : null
                }
                label={
                  isOccupied
                    ? isPartialStay
                      ? 'Por Horas'
                      : 'Ocupada'
                    : isPendingToday
                    ? 'Reserva Pendiente'
                    : housekeepingStatus === 'dirty'
                    ? 'Limpieza Requerida'
                    : housekeepingStatus === 'in-progress'
                    ? 'En Limpieza'
                    : housekeepingStatus === 'out-of-service'
                    ? 'Mantenimiento'
                    : housekeepingStatus === 'do-not-disturb'
                    ? 'No Molestar'
                    : 'Disponible'
                }
                size="small"
                color={statusConfig.chip}
                sx={{
                  fontWeight: 500,
                  '& .MuiChip-icon': {
                    fontSize: '1rem'
                  }
                }}
              />
            </Box>

            {/* Temporizador para habitaciones ocupadas */}
            {isOccupied && reservation?.checkOutDate && (
              <Box sx={{ mt: 1 }}>
                <ProfessionalCountdownTimer checkOutTime={reservation.checkOutDate} stayType={reservation.stayType} />
              </Box>
            )}

            {/* Info de reserva pendiente para HOY */}
            {isPendingToday && (
              <Tooltip title={`Huésped: ${pendingReservation.fullName || 'N/A'}`}>
                <Box sx={{ mt: 1, p: 1, borderRadius: 1, backgroundColor: 'rgba(63, 81, 181, 0.06)', border: '1px dashed', borderColor: '#7986CB' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: '#283593' }}>
                    Check-in: {new Date(pendingReservation.checkInDate).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}
                    {pendingReservation.stayType !== 'night' && ` ${new Date(pendingReservation.checkInDate).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`}
                  </Typography>
                  {pendingReservation.fullName && pendingReservation.fullName.trim() !== '' && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {pendingReservation.fullName}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            )}

            {/* Indicador sutil de reserva FUTURA (no bloquea la habitación) */}
            {isPendingFuture && (
              <Tooltip title={`Reserva futura: ${new Date(pendingReservation.checkInDate).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}${pendingReservation.fullName ? ' — ' + pendingReservation.fullName : ''}`}>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarTodayIcon sx={{ fontSize: '0.8rem', color: '#7986CB' }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#5C6BC0', fontWeight: 500 }}>
                    Reserva: {new Date(pendingReservation.checkInDate).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}
                  </Typography>
                </Box>
              </Tooltip>
            )}

            {/* Temporizador para habitaciones sucias */}
            {!isOccupied && housekeepingStatus === 'dirty' && room.dirtySince && (
              <Box sx={{ mt: 1 }}>
                <DirtyRoomTimer dirtySince={room.dirtySince} />
              </Box>
            )}
          </Stack>
        </Box>
      </Paper>
    </Fade>
  );
};

// ==============================|| DASHBOARD PRINCIPAL PROFESIONAL ||============================== //

const MainDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));

  // Estados
  const [isLoading, setLoading] = useState(true);
  const [isRefreshing, setRefreshing] = useState(false);
  const [roomData, setRoomData] = useState([]);
  const [reservationData, setReservationData] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [foodItemData, setItemsData] = useState([]);
  const [roomReservations, setRoomReservations] = useState({});
  const [pendingMap, setPendingMap] = useState({});
  const [cleaningBufferMinutes, setCleaningBufferMinutes] = useState(30);
  const [viewMode, setViewMode] = useState('grid');
  const [stayTypes, setStayTypes] = useState([]);
  const [nightlyType, setNightlyType] = useState(null);

  const hotel = JSON.parse(localStorage.getItem('hotelData'));

  // Funciones de API mejoradas con manejo de errores
  const fetchRoomData = async () => {
    try {
      const response = await getApi(`api/room/viewallrooms/${hotel?.hotelId}`);
      const sortedRooms = response?.data?.sort((a, b) => a.roomNo - b.roomNo);
      setRoomData(sortedRooms);
      return sortedRooms;
    } catch (error) {
      console.error('Error al obtener habitaciones:', error);
      return [];
    }
  };

  const fetchReservationData = async () => {
    try {
      const response = await getApi(`api/reservation/viewallactivereservations/${hotel?.hotelId}`);
      setReservationData(response?.data?.reservationData);
      return response?.data?.reservationData || [];
    } catch (error) {
      console.error('Error al obtener reservaciones:', error);
      return [];
    }
  };

  const fetchCustomerData = async () => {
    try {
      const response = await getApi(`api/customer/viewallcustomer/${hotel?.hotelId}`);
      setCustomerData(response?.data?.customerData);
    } catch (error) {
      console.error('Error al obtener clientes:', error);
    }
  };

  const fetchItemsData = async () => {
    try {
      const response = await getApi(`api/restaurant/viewallitems/${hotel?.hotelId}`);
      setItemsData(response?.data?.restaurantData);
    } catch (error) {
      console.error('Error al obtener artículos:', error);
    }
  };

  const fetchStayTypes = async () => {
    try {
      const response = await getApi(`api/hotel/stay-types/list/${hotel?.hotelId}`);
      if (response && response.data) {
        setStayTypes(response.data);
        const nightly = response.data.find((t) => t.isNightly);
        setNightlyType(nightly);
      }
    } catch (error) {
      console.error('Error fetching stay types', error);
    }
  };

  const fetchPendingReservationsMap = async () => {
    try {
      const response = await getApi(`api/reservation/pendingreservationsmap/${hotel?.hotelId}`);
      setPendingMap(response?.data?.pendingMap || {});
    } catch (error) {
      console.error('Error al obtener reservas pendientes:', error);
    }
  };

  const fetchHotelBuffer = async () => {
    try {
      const response = await getApi(`api/hotel/view/${hotel?.hotelId}`);
      setCleaningBufferMinutes(response?.data?.cleaningBufferMinutes ?? 30);

    } catch (error) {
      console.error('Error al obtener buffer del hotel:', error);
    }
  };

  const fetchRoomReservationDetails = async () => {
    try {
      const rooms = await fetchRoomData();
      const reservations = await fetchReservationData();

      const roomReservationMap = {};

      for (const room of rooms) {
        if (room.bookingStatus === 'active') {
          const reservation = reservations.find((res) => String(res.roomNo) === String(room.roomNo));

          if (reservation) {
            roomReservationMap[room.roomNo] = {
              checkInDate: reservation.checkInDate,
              checkOutDate: reservation.checkOutDate,
              stayType: reservation.stayType,
              housekeepingStatus: reservation.housekeepingStatus || room.housekeepingStatus || 'clean'
            };
          }
        } else {
          roomReservationMap[room.roomNo] = {
            housekeepingStatus: room.housekeepingStatus || 'clean'
          };
        }
      }

      setRoomReservations(roomReservationMap);
      await fetchPendingReservationsMap();
      await fetchHotelBuffer();
    } catch (error) {
      console.error('Error al crear mapeo de reservas:', error);
    }
  };

  const fetchReservationIdForActiveRooms = async (roomNo) => {
    try {
      const response = await getApi(`api/room/activroomreservationid/${hotel?.hotelId}/${roomNo}`);

      const reservationId = response?.data?.[0]?.reservationId;

      if (!reservationId) {
        // No hay reserva → crear una nueva
        navigate(`/dashboard/reservation/reserve-room/${roomNo}`);
        return;
      }

      // Hay reserva → mostrar detalles
      navigate(`/dashboard/reservation/view/${reservationId}`);
    } catch (error) {
      console.error('Error fetching reservation ID:', error);
    }
  };

  // Manejadores

  const handleRoomClick = (roomInfo) => {
    setTimeout(() => {
      if (roomInfo.bookingStatus === 'active') {
        fetchReservationIdForActiveRooms(roomInfo.roomNo);
      } else {
        const pending = pendingMap[String(roomInfo.roomNo)];
        if (pending && pending._id) {
          const now = new Date();
          const pendingCheckIn = new Date(pending.checkInDate);
          const blockThreshold = new Date(pendingCheckIn.getTime() - (cleaningBufferMinutes * 60 * 1000));

          if (now >= blockThreshold) {
            // Ya es hora de bloquear → ir al detalle de la reserva
            navigate(`/dashboard/reservation/view/${pending._id}`);
          } else {
            // Aún falta tiempo → permitir crear nueva reserva
            navigate(`/dashboard/reservation/reserve-room/${roomInfo.roomNo}`);
          }
        } else {
          navigate(`/dashboard/reservation/reserve-room/${roomInfo.roomNo}`);
        }
      }
    }, 300);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchRoomReservationDetails();
    setTimeout(() => setRefreshing(false), 1000);
  };

  // Efectos
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchCustomerData(), fetchItemsData(), fetchStayTypes()]);
        await fetchRoomReservationDetails();
      } catch (error) {
        console.error('Error al cargar datos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();

    const interval = setInterval(() => {
      fetchRoomReservationDetails();
    }, 30000);

    // Listen for reservation updates
    const handleReservationUpdate = () => {
      fetchRoomReservationDetails();
    };
    window.addEventListener('reservationUpdated', handleReservationUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('reservationUpdated', handleReservationUpdate);
    };
  }, []);

  // Estadísticas de habitaciones
  const roomStats = {
    total: roomData?.length || 0,
    occupied: roomData?.filter((r) => r.bookingStatus === 'active').length || 0,
    pending: roomData?.filter((r) => {
      const p = pendingMap[String(r.roomNo)];
      if (!p || r.bookingStatus === 'active') return false;
      const now = new Date();
      const pCheckIn = new Date(p.checkInDate);
      const blockThreshold = new Date(pCheckIn.getTime() - (cleaningBufferMinutes * 60 * 1000));
      return now >= blockThreshold;
    }).length || 0,
    available: roomData?.filter((r) => {
      if (r.bookingStatus === 'active' || r.housekeepingStatus !== 'clean') return false;
      const p = pendingMap[String(r.roomNo)];
      if (!p) return true;
      const now = new Date();
      const pCheckIn = new Date(p.checkInDate);
      const blockThreshold = new Date(pCheckIn.getTime() - (cleaningBufferMinutes * 60 * 1000));
      return now < blockThreshold; // Not yet time to block
    }).length || 0,
    dirty: roomData?.filter((r) => r.housekeepingStatus === 'dirty').length || 0,
    maintenance: roomData?.filter((r) => r.housekeepingStatus === 'out-of-service').length || 0
  };

  return (
    <>
      <Grid container spacing={gridSpacing}>
        {/* Tarjetas de Resumen Mejoradas */}
        <Grid item xs={12}>
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 700, color: theme.palette.text.primary }}>
            Centro de Control
          </Typography>
          <Grid container spacing={gridSpacing}>
            <Grid item lg={3} md={6} sm={6} xs={12}>
              <Box
                onClick={() => navigate('/dashboard/roommanagement')}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { transform: 'translateY(-8px)' }
                }}
              >
                <TotalRoomsCard isLoading={isLoading} roomData={roomData} />
              </Box>
            </Grid>

            <Grid item lg={3} md={6} sm={6} xs={12}>
              <Box
                onClick={() => navigate('/dashboard/customers')}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { transform: 'translateY(-8px)' }
                }}
              >
                <TotalCustomersCard isLoading={isLoading} customerData={customerData} />
              </Box>
            </Grid>

            <Grid item sm={6} xs={12} md={6} lg={3}>
              <Box
                onClick={() => navigate('/dashboard/restaurant')}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { transform: 'translateY(-8px)' }
                }}
              >
                <FoodItems isLoading={isLoading} foodItemData={foodItemData} />
              </Box>
            </Grid>

            <Grid item sm={6} xs={12} md={6} lg={3}>
              <Box
                onClick={() => navigate('/dashboard/reservation')}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { transform: 'translateY(-8px)' }
                }}
              >
                <TotalReservationCard isLoading={isLoading} reservationData={reservationData} />
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {/* Panel de Habitaciones Profesional */}
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{
              borderRadius: '12px',
              boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.03)',
              overflow: 'hidden',
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            {/* Encabezado del Panel */}
            <Box
              sx={{
                p: 2.5,
                backgroundColor: theme.palette.background.paper,
                borderBottom: `1px solid ${theme.palette.divider}`
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Estado de Habitaciones
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Chip label={`${roomStats.total} totales`} size="small" sx={{ fontWeight: 600 }} />
                    <Typography variant="body2" color="text.secondary">
                      {roomStats.occupied} ocupadas • {roomStats.pending > 0 ? `${roomStats.pending} pendientes • ` : ''}{roomStats.available} disponibles
                    </Typography>
                  </Stack>
                </Box>

                <Stack direction="row" spacing={1}>
                  <Tooltip title="Actualizar">
                    <IconButton
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        '&:hover': { backgroundColor: theme.palette.action.hover }
                      }}
                    >
                      <RefreshIcon
                        sx={{
                          animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }}
                      />
                    </IconButton>
                  </Tooltip>

                  {!matchDownSM && (
                    <Tooltip title="Cambiar vista">
                      <IconButton
                        onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          '&:hover': { backgroundColor: theme.palette.action.hover }
                        }}
                      >
                        {viewMode === 'grid' ? <ViewListIcon /> : <GridViewIcon />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>

              {/* Leyenda de Estados */}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                {Object.entries(getStatusConfiguration('clean', theme)).length > 0 && (
                  <>
                    <Chip
                      icon={<KingBedIcon />}
                      label="Disponible"
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: theme.palette.success.main,
                        color: theme.palette.success.main,
                        '& .MuiChip-icon': { color: theme.palette.success.main }
                      }}
                    />
                    <Chip
                      icon={<KingBedIcon />}
                      label="Ocupada"
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: theme.palette.error.main,
                        color: theme.palette.error.main,
                        '& .MuiChip-icon': { color: theme.palette.error.main }
                      }}
                    />
                    <Chip
                      icon={<HourglassEmptyIcon />}
                      label="Reserva Pendiente"
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: '#7986CB',
                        color: '#283593',
                        '& .MuiChip-icon': { color: '#3F51B5' }
                      }}
                    />
                    <Chip
                      icon={<CleaningServicesIcon />}
                      label="Requiere Limpieza"
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: theme.palette.warning.main,
                        color: theme.palette.warning.dark,
                        '& .MuiChip-icon': { color: theme.palette.warning.main }
                      }}
                    />
                    <Chip
                      icon={<HourglassEmptyIcon />}
                      label="Pendiente (Sucia)"
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: '#D84315',
                        color: '#BF360C',
                        '& .MuiChip-icon': { color: '#D84315' }
                      }}
                    />
                    <Chip
                      icon={<BuildIcon />}
                      label="Mantenimiento"
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: theme.palette.grey[600],
                        color: theme.palette.grey[700],
                        '& .MuiChip-icon': { color: theme.palette.grey[600] }
                      }}
                    />
                  </>
                )}
              </Stack>
            </Box>

            {/* Contenido de Habitaciones */}
            <Box sx={{ p: 3 }}>
              {isLoading ? (
                <Grid container spacing={2}>
                  {[...Array(8)].map((_, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          borderRadius: 3,
                          height: 140
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : roomData?.length > 0 ? (
                <Grid container spacing={2}>
                  {roomData?.map((room) => {
                    const reservation = roomReservations[room.roomNo];
                    const pendingReservation = pendingMap[String(room.roomNo)];

                    return (
                      <Grid item key={room._id} xs={12} sm={6} md={4} lg={3}>
                        <ProfessionalRoomCard
                          room={room}
                          reservation={reservation}
                          pendingReservation={pendingReservation}
                          onClick={() => handleRoomClick(room)}
                          theme={theme}
                          nightlyType={nightlyType}
                          cleaningBufferMinutes={cleaningBufferMinutes}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                    px: 2
                  }}
                >
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.action.hover,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3
                    }}
                  >
                    <AddCircleIcon
                      sx={{
                        fontSize: 60,
                        color: theme.palette.text.secondary
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                      mb: 1
                    }}
                  >
                    No hay habitaciones registradas
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      textAlign: 'center',
                      maxWidth: 400
                    }}
                  >
                    Comience agregando las habitaciones de su hotel para gestionar reservaciones y disponibilidad
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default MainDashboard;
